// 管理 API 的最小封装。所有请求经 /api 前缀，开发时由 Vite 代理到 admin 角色。

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

let onUnauthorized: () => void = () => undefined;

/** 会话失效时的处理，由应用外壳注册：清掉当前用户，路由守卫随即回到登录页（docs/04 第 9 节）。 */
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await request(path, {
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    credentials: "same-origin",
    ...init,
  });
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** 下载接口返回的文件并以 filename 存到本地；失败时与 api 一样抛 ApiError，页面可以提示原因（如导出行数超限）。 */
export async function download(path: string, filename: string): Promise<void> {
  const blob = await (await request(path, { credentials: "same-origin" })).blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  // 部分浏览器在 click 返回时还没开始读 blob，立即释放会取消下载，稍后再释放
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** 发请求并把失败转成 ApiError。 */
async function request(path: string, init: RequestInit): Promise<Response> {
  const res = await fetch(`/api${path}`, init);
  // 登录接口自己的 401 是"用户名或密码错误"，不是会话失效
  if (res.status === 401 && !path.startsWith("/auth/")) onUnauthorized();
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { /* 非 JSON 响应 */ }
    // 错误体是 {"error": "..."}，见 docs/04-管理接口.md 第 1 节。
    const msg = (body as { error?: string } | undefined)?.error ?? `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, msg, body);
  }
  return res;
}

/** 把请求失败转成给人看的文案，用于表单顶部的 Alert。 */
export function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
